import { EchartsWithTheme, useTheme } from '@maxtropy/components';
import { COLORS } from '../constants';
import { ChartData, DateType, DateValue } from '../types';
import dayjs from 'dayjs';
import { isNil } from 'lodash-es';
import { useRef, useMemo, useEffect } from 'react';
import { EchartsWithThemeRef } from '@maxtropy/components/es/components/EchartsWithTheme';
import { keepPercentOrLine, keepTwoNull } from '@/utils/utils';

export const getTimeAxis = (dateType: DateType, time: number) => {
  switch (dateType) {
    case DateType.HOUR:
      return dayjs(time).format('YYYY-MM-DD HH:mm');
    case DateType.DAY:
      return dayjs(time).format('YYYY-MM-DD');
    default:
      return '--';
  }
};

const lineLegendIcon = 'path://M0,0 L12,0 L12,4 L0,4 Z';
const bgLegendIcon = 'path://M0,0 L10,0 L10,12 L0,12 Z';

// 构建基于时间戳的 markArea 数据
const buildTimeBasedAreas = (chartData: ChartData[]) => {
  const longAreas: any[] = [];
  const nonLongAreas: any[] = [];

  if (chartData.length === 0) return { longAreas, nonLongAreas };

  // 计算采样间隔（假设数据基本等间隔，取前两个点的差值）
  let interval = 3600000; // 默认1小时（毫秒），避免除零
  if (chartData.length > 1) {
    interval = chartData[1].time - chartData[0].time;
  }
  const halfInterval = interval / 2;

  let start = 0;
  let curr = chartData[0].flag2;

  for (let i = 1; i < chartData.length; i++) {
    if (chartData[i].flag2 !== curr) {
      const from = chartData[start].time - halfInterval;
      const to = chartData[i - 1].time + halfInterval;
      if (curr) longAreas.push([{ xAxis: from }, { xAxis: to }]);
      else nonLongAreas.push([{ xAxis: from }, { xAxis: to }]);
      start = i;
      curr = chartData[i].flag2;
    }
  }

  // 最后一个区域
  const from = chartData[start].time - halfInterval;
  const to = chartData[chartData.length - 1].time + halfInterval;
  if (curr) longAreas.push([{ xAxis: from }, { xAxis: to }]);
  else nonLongAreas.push([{ xAxis: from }, { xAxis: to }]);

  return { longAreas, nonLongAreas };
};
const hourTooltipFormatter = {
  trigger: 'axis',
  formatter: (params: any) => {
    const raw = params[0]?.data[2]; // 原始数据在数组第3个元素
    const dateLabel = params[0]?.axisValue ?? '';
    let result = `<div style="color:var(--mx-text-base-color);margin-bottom:8px">${dayjs(dateLabel).format(
      'YYYY-MM-DD HH:mm'
    )}</div>`;

    result += `<div style="display:flex;flex-direction: column;gap:8px;">
              <div><span style="color:var(--mx-text-base-color)">累积流量: ${keepTwoNull(
                raw?.totalAmount
              )}m³</span></div>
              <div><span style="color:var(--mx-text-base-color)">压强: ${keepTwoNull(raw?.pressure)}kPa</span></div>
            </div>`;
    return result;
  },
};

const dayTooltipFormatter = {
  trigger: 'axis',
  formatter: (params: any) => {
    const dateLabel = params[0]?.axisValue ?? '';
    let result = `<div style="color:var(--mx-text-base-color);margin-bottom:8px">${dayjs(Number(dateLabel)).format(
      'YYYY-MM-DD'
    )}</div>`;

    params.forEach((param: any, index: number) => {
      if (index === 2) return; // 跳过趋势线（如果系列中有三条）
      const raw = param.data.raw; // 原始数据
      const value = param.data.value; // 当前系列数值
      const proportion = param.data.proportion; // 占比（如果系列有）

      if (index === 0) {
        result += `<div style="display:flex;align-items:center;gap:8px;margin-top:4px;margin-left: 18px">
              <span style="color:var(--mx-text-base-color)">总用量：${keepTwoNull(raw?.totalAmount) ?? 0}m³</span>
            </div>`;
      }
      result += `<div style="display:flex;align-items:center;gap:8px;margin-top:4px">
              <div style="width:10px;height:10px;background:${param.color};border-radius:10px;"></div>
              <span style="color:var(--mx-text-base-color)">${param.seriesName}: ${keepTwoNull(value)}m³</span>
              <span style="color:var(--mx-text-base-color)">占比: ${keepPercentOrLine(proportion)}%</span>
            </div>`;
    });

    return result;
  },
};

const HourChartOption = (chartData: ChartData[], pageLabel?: string) => {
  const { longAreas, nonLongAreas } = buildTimeBasedAreas(chartData);

  return {
    grid: {
      left: 20,
      right: 20,
      top: 70,
      containLabel: true,
    },
    legend: {
      top: 10,
      right: 56,
      itemWidth: 12,
      itemHeight: 12,
      data: [
        { name: `非${pageLabel}监测时段`, icon: bgLegendIcon },
        { name: `${pageLabel}监测时段`, icon: bgLegendIcon },
        { name: `非${pageLabel}用量`, icon: 'rect' },
        { name: `${pageLabel}用量`, icon: 'rect' },
        { name: '压强', icon: lineLegendIcon },
      ],
    },
    tooltip: hourTooltipFormatter,
    yAxis: [
      {
        type: 'value',
        name: 'm³',
        alignTicks: true,
        axisLabel: { formatter: '{value}' },
      },
      {
        type: 'value',
        name: 'kPa',
        alignTicks: true,
        axisLabel: { formatter: '{value}' },
      },
    ],
    xAxis: {
      type: 'time', // 改为 time 轴
      axisLabel: {
        formatter: (value: number) => {
          const date = dayjs(value);
          return date.format('MM-DD HH:mm');
        },
      },
    },
    series: [
      {
        type: 'bar',
        name: `${pageLabel}用量`,
        stack: 'total',
        barMaxWidth: 20,
        barMinWidth: 4,
        data: chartData.map(d => [d.time, d.longFlowAmount, d]), // 改为数组
        color: COLORS.longFlow,
      },
      {
        type: 'line',
        name: `${pageLabel}监测时段`,
        barMinWidth: 0,
        color: COLORS.longFlowBg,
        markArea: {
          silent: true,
          itemStyle: { color: COLORS.longFlowBg },
          data: longAreas,
        },
      },
      {
        type: 'bar',
        name: `非${pageLabel}用量`,
        stack: 'total',
        barMaxWidth: 20,
        barMinWidth: 4,
        data: chartData.map(d => [d.time, d.nonLongFlowAmount, d]),
        color: COLORS.nonLongFlow,
        markArea: {
          silent: true,
          itemStyle: { color: COLORS.nonLongFlowBg },
          data: nonLongAreas,
        },
      },
      {
        type: 'line',
        name: `非${pageLabel}监测时段`,
        barMinWidth: 0,
        color: COLORS.nonLongFlowBg,
        markArea: {
          silent: true,
          itemStyle: { color: COLORS.nonLongFlowBg },
          data: nonLongAreas,
        },
      },
      {
        type: 'line',
        name: '压强',
        yAxisIndex: 1,
        data: chartData.map(d => [d.time, d.pressure, d]),
        smooth: true,
        color: COLORS.pressure,
        lineStyle: { width: 2 },
        showSymbol: false,
      },
    ],
  };
};

const DayChartOption = (chartData: ChartData[], pageLabel?: string) => {
  return {
    legend: {
      top: 10,
      right: 56,
      itemWidth: 12,
      itemHeight: 12,
      data: [
        { name: `非${pageLabel}用量`, icon: 'rect' },
        { name: `${pageLabel}用量`, icon: 'rect' },
        { name: `${pageLabel}用量趋势`, icon: lineLegendIcon },
      ],
    },
    tooltip: dayTooltipFormatter,
    yAxis: [
      {
        type: 'value',
        name: 'm³',
      },
    ],
    xAxis: {
      type: 'category',
      data: chartData.map(d => d.time),
      axisLabel: {
        formatter: (value: number) => {
          return dayjs(Number(value)).format('MM-DD');
        },
      },
    },
    series: [
      {
        type: 'bar',
        name: `${pageLabel}用量`,
        stack: 'total',
        barMaxWidth: 20,
        barMinWidth: 4,
        data: chartData.map(d => ({
          value: d.longFlowAmount,
          proportion: d.longFlowProportion,
          raw: d,
        })),
        color: COLORS.longFlow,
        z: 2,
      },
      {
        type: 'bar',
        name: `非${pageLabel}用量`,
        stack: 'total',
        data: chartData.map(d => ({
          value: d.nonLongFlowAmount,
          proportion: d.nonLongFlowProportion,
          raw: d,
        })),
        color: COLORS.nonLongFlow,
      },
      {
        type: 'line',
        name: `${pageLabel}用量趋势`,
        data: chartData.map(d => ({
          value: d.longFlowAmount,
          raw: d,
        })),
        color: COLORS.trendLine,
        smooth: true,
        showSymbol: false,
        z: 3,
      },
    ],
  };
};

const getChartOption = (
  dateType: DateType,
  chartData: ChartData[],
  queryTimeRanges?: DateValue,
  pageLabel?: string
) => {
  return {
    grid: {
      right: 0,
      top: 70,
      containLabel: true,
    },
    toolbox: {
      top: 10,
      feature: {
        brush: {
          type: ['lineX', 'clear'],
          title: { lineX: '框选', clear: '清除' },
        },
      },
    },
    brush: {
      toolbox: ['lineX', 'clear'],
      xAxisIndex: 0,
    },
    dataZoom: [
      {
        type: 'slider',
        left: 50,
        right: 10,
        height: 20,
        textStyle: { fontSize: 10 },
      },
    ],
    ...(dateType === DateType.HOUR ? HourChartOption(chartData, pageLabel) : DayChartOption(chartData, pageLabel)),
  };
};

interface ChartProps {
  pageLabel?: string;
  chartData: ChartData[];
  queryTimeRanges?: DateValue;
  dateType: DateType;
}

const Chart = ({ pageLabel, chartData, queryTimeRanges, dateType }: ChartProps) => {
  const theme = useTheme();
  const chartRef = useRef<EchartsWithThemeRef>();

  const options = useMemo(
    () => getChartOption(dateType, chartData, queryTimeRanges, pageLabel),
    [dateType, chartData, queryTimeRanges, pageLabel]
  );

  useEffect(() => {
    const handleResize = () => {
      chartRef.current?.getInstance()?.resize();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <EchartsWithTheme
      theme={theme}
      ref={d => {
        if (d) chartRef.current = d;
      }}
      option={options}
      onEvents={{
        brushSelected: (params: any) => {
          var brushed: any[] = [];
          let startTime;
          let endTime;
          var brushComponent = params.batch[0];
          const chartObject = chartRef.current?.getInstance();
          const series = chartObject?.getOption()?.series as any[];

          brushComponent.selected.forEach((item: any, index: number) => {
            const findSeries = series?.find(i => i.name === item.seriesName);
            if (!findSeries) return;

            if (dateType === DateType.HOUR && index !== 0) return; // 小时视图只统计第一个系列（累积流量）

            let arr: any[] = [];
            let totalArr: any[] = [];

            item.dataIndex.forEach((c: number, idx: number) => {
              const dataPoint = findSeries.data[c];
              let time, value, raw;

              if (dateType === DateType.HOUR) {
                // 时间轴二维数组
                time = dataPoint[0];
                value = dataPoint[1];
                raw = dataPoint[2];
              } else {
                // 类目轴对象
                value = dataPoint.value;
                raw = dataPoint.raw;
                time = raw.time; // 从原始数据获取时间戳
              }

              if (idx === 0) {
                startTime = getTimeAxis(dateType, time);
              }
              if (idx === item.dataIndex.length - 1) {
                endTime = getTimeAxis(dateType, time);
              }
              arr.push(value ?? 0);
              totalArr.push(raw?.totalAmount ?? 0);
            });

            if (arr.filter(x => !isNil(x)).length > 0) {
              const totalArrData = totalArr.filter(x => !isNil(x)).reduce((a, b) => a + b, 0);
              if (dateType === DateType.DAY) {
                const arrData = arr.filter(x => !isNil(x)).reduce((a, b) => a + b, 0);
                if (index === 0) {
                  brushed.push('总用量（累计）：' + (!isNil(totalArrData) ? totalArrData.toFixed(2) : '--') + 'm³');
                }
                brushed.push(item.seriesName + '（累计）：' + (!isNil(arrData) ? arrData.toFixed(2) : '--') + 'm³');
              } else if (dateType === DateType.HOUR) {
                brushed.push('累积流量（累计）：' + (!isNil(totalArrData) ? totalArrData.toFixed(2) : '--') + 'm³');
              }
            }
          });

          if (brushComponent.areas.length > 0) {
            chartObject?.setOption({
              title: {
                show: true,
                padding: 8,
                backgroundColor: 'rgba(0,0,0,0.8)',
                borderColor: 'transparent',
                borderRadius: 2,
                textStyle: { color: 'var(--mx-text-base-color)', fontSize: 12, fontWeight: 500 },
                text: startTime + ' ~ ' + endTime + '\n\n' + brushed.join('\n\n'),
                top: 100,
                left: brushComponent.areas[0].range[1] - 240,
              },
            });
          } else {
            chartObject?.setOption({ title: { show: false } });
          }
        },
      }}
      empty={{ isShowEmpty: chartData.length === 0 }}
      style={{ height: 460, width: '100%' }}
    />
  );
};

export default Chart;
